import {SimpleScene} from "@client/components/SimpleScene";
import {getIsLoading} from "@client/features/scenes";
import type {
    Locator,
    RouterRenderPropsArgs,
    TransitionFn,
} from "@client/gotterdammerung";
import * as routes from "@client/journeys/entries/routes";
import * as scenes from "@client/journeys/entries/scenes";
import * as exercises from "@client/journeys/exercises";
import * as onboarding from "@client/journeys/onboarding";
import {useSiteContext} from "@client/site/context";
import {useAppSelector} from "@client/store";
import {zIndexClasses} from "@client/style";
import {Dialog, Transition} from "@headlessui/react";
import {classNames} from "@reactivated";
import React from "react";

export function SceneModal(props: {
    isOpen: boolean;
    setOpen: (state: boolean) => void;
    children: React.ReactNode;
}) {
    const {realm} = useSiteContext();

    return (
        <Transition.Root show={props.isOpen} as={React.Fragment}>
            {realm == "site" && (
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className={`fixed inset-0 bg-gray-500/75 transition-opacity max-sm:hidden ${zIndexClasses.sceneModalBackdrop} `}
                    />
                </Transition.Child>
            )}
            {/* z-index above the left hand menu on the manager side */}

            <Dialog
                as="div"
                className={`relative ${zIndexClasses.sceneModal} `}
                onClose={() => {
                    const targetElement = event?.target as Element | undefined;
                    if (targetElement?.closest(".joy-notification")) {
                        return;
                    }
                    props.setOpen(false);
                }}
            >
                <div
                    style={{maxWidth: 900}}
                    className={classNames(
                        "fixed mx-auto",
                        realm == "site"
                            ? "inset-0 sm:inset-4 sm:overflow-hidden sm:rounded-xl"
                            : "inset-0",
                    )}
                >
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="flex h-full flex-col overflow-hidden bg-white">
                            {props.children}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export function OnboardingJourney(
    props: onboarding.Input & {close: (result: onboarding.Output) => void},
) {
    const isLoading = useAppSelector(getIsLoading);

    return (
        <onboarding.InputContext.Provider value={props}>
            <onboarding.Hooks>
                <onboarding.Route pathname="/">
                    {({view, options, parent, locator, tabs, actions}) => (
                        <SimpleScene
                            view={view}
                            options={options}
                            parent={parent}
                            locator={locator}
                            tabs={tabs}
                            actions={actions}
                            close={props.close}
                            isLoading={isLoading}
                            isResolving={false}
                            transition={onboarding.router.transition}
                        />
                    )}
                </onboarding.Route>
            </onboarding.Hooks>
        </onboarding.InputContext.Provider>
    );
}

export function ExercisesJourney(
    props: exercises.Input & {close: (result: exercises.Output) => void},
) {
    const isLoading = useAppSelector(getIsLoading);

    return (
        <exercises.InputContext.Provider value={props}>
            <exercises.Hooks>
                <exercises.Route pathname="/">
                    {({view, options, parent, locator, tabs, actions}) => (
                        <SimpleScene
                            view={view}
                            options={options}
                            parent={parent}
                            locator={locator}
                            tabs={tabs}
                            actions={actions}
                            close={props.close}
                            isLoading={isLoading}
                            isResolving={false}
                            transition={exercises.router.transition}
                        />
                    )}
                </exercises.Route>
            </exercises.Hooks>
        </exercises.InputContext.Provider>
    );
}

// Private factory - NOT exported to maintain HMR boundary
type AnyRouter = {
    _input: unknown;
    _output: unknown;
    _implementations: unknown;
    _routes: Array<{definition: {name: string; tabs: string[]}}>;
    // oxlint-disable-next-line typescript/no-explicit-any
    mount: (implementations: never) => {Route: React.ComponentType<any>};
    transition: TransitionFn;
    // oxlint-disable-next-line typescript/no-explicit-any
    locator: (...args: any[]) => Locator;
};

type RouteAndTab<TRouter extends AnyRouter> = {
    [K in keyof TRouter["_routes"] & number]: TRouter["_routes"][K] extends {
        definition: {
            name: infer TName extends string;
            tabs: infer TTabs extends string[];
        };
    }
        ? `${TName}.${TTabs[number]}`
        : never;
}[keyof TRouter["_routes"] & number];

type JourneyProps<TRouter extends AnyRouter> = TRouter["_input"] & {
    route?: RouteAndTab<TRouter>;
    close: (result: TRouter["_output"] | null) => void;
};

function createJourney<TRouter extends AnyRouter>(
    router: TRouter,
    implementations: TRouter["_implementations"],
) {
    const {Route} = router.mount(implementations as never);
    const {name, tabs} = router._routes[0].definition;
    const defaultRoute = `${name}.${tabs[0]}`;
    return function Journey(props: JourneyProps<TRouter>) {
        const {close, route, ...input} = props;
        const isLoading = useAppSelector(getIsLoading);
        const pathname = router.locator({
            route: route ?? defaultRoute,
            params: {},
        }).url;

        return (
            <Route pathname={pathname} input={input} close={close}>
                {({
                    view,
                    options,
                    parent,
                    locator,
                    tabs,
                    actions,
                }: RouterRenderPropsArgs) => (
                    <SimpleScene
                        view={view}
                        options={options}
                        parent={parent}
                        locator={locator}
                        tabs={tabs}
                        actions={actions}
                        close={close}
                        isLoading={isLoading}
                        isResolving={false}
                        transition={router.transition}
                    />
                )}
            </Route>
        );
    };
}

export const EntryJourney = createJourney(routes.router, {
    search: scenes.Search,
    create: scenes.Create,
    create_food: scenes.CreateFood,
    barcode_found_confirm_entry: scenes.BarcodeFoundConfirmEntry,
    barcode_found_make_corrections: scenes.BarcodeFoundMakeCorrections,
    barcode_not_found_create_food: scenes.BarcodeNotFoundCreateFood,
    barcode_not_found_confirm_entry: scenes.BarcodeNotFoundConfirmEntry,
});
